import { snakeCase } from 'change-case'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useAllAlerts } from '@/feature/alerts/api/useAllAlerts'
import { useCreateAlert } from '@/feature/alerts/api/useCreateAlert'
import { useUpdateAlert } from '@/feature/alerts/api/useUpdateAlert'
import { type AlertTemplateFieldValues } from '@/feature/alerts/constants'
import { useAlertStore } from '@/feature/alerts/store'
import { useGetUser } from '@/feature/auth'
import { useFlag } from '@/feature/feature-flags'
import { useTagsStore } from '@/feature/tags'

import {
  type AlertScope,
  buildVehicleTagsUnsupportedMessage,
  contactEntrySchema,
  emailSchema,
  type FormErrors,
  parseApiErrors,
  resolveAlertScope,
} from './coreAlertUtils'

export const useCoreAlertForm = () => {
  const { isCoreAlertOpen, closeCoreAlert, closeCreateAlert, selectedTemplate, editingAlert } =
    useAlertStore()
  const { data: user } = useGetUser()
  const { trigger: createTrigger, isMutating: isCreating } = useCreateAlert()
  const { trigger: updateTrigger, isMutating: isUpdating } = useUpdateAlert(
    editingAlert?.id ?? null,
  )
  const { mutate: mutateAlerts } = useAllAlerts()

  const isTagsEnabled = useFlag('assetTags', false)
  const { alertTags, setAlertTags, toggleAlertTagsPopout, openModal } = useTagsStore()

  const tagIconRef = useRef<HTMLDivElement>(null)

  const isEditMode = !!editingAlert
  const isMutating = isCreating || isUpdating

  // Two gates: the template must follow a specific vehicle, and the org must
  // have the tags feature turned on.
  const taggableAlert = !!selectedTemplate?.taggableAlert && isTagsEnabled

  // Shown in place of the selector, so the user learns why tagging is missing
  // rather than finding a gap. Stays null when the feature is off entirely —
  // an org without tags should see nothing about them.
  const tagsUnsupportedMessage =
    isTagsEnabled && selectedTemplate && !selectedTemplate.taggableAlert
      ? buildVehicleTagsUnsupportedMessage(selectedTemplate.title)
      : null

  const [name, setName] = useState('')
  const [contacts, setContacts] = useState('')
  const [fieldValues, setFieldValues] = useState<AlertTemplateFieldValues>({})
  const [alertScope, setAlertScope] = useState<AlertScope>('allAssets')
  const [errors, setErrors] = useState<FormErrors>({})
  const [fieldErrorKeys, setFieldErrorKeys] = useState<Set<string>>(new Set())

  // The selector is for narrowing to tagged vehicles; a global alert has no use
  // for it, so it only appears once the user opts into that scope.
  const showTagSelector = taggableAlert && alertScope === 'assetsWithTags'

  const resolvedFieldValues = useMemo(
    () => ({ ...selectedTemplate?.defaultFieldValues, ...fieldValues }),
    [fieldValues, selectedTemplate?.defaultFieldValues],
  )

  const handleFieldChange = (key: string, value: number) => {
    setFieldValues((prev: AlertTemplateFieldValues) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, fields: undefined }))
    setFieldErrorKeys((prev) => {
      if (!prev.has(key)) return prev
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }

  const handleAlertScopeChange = (value: string) => {
    const scope = value as AlertScope
    setAlertScope(scope)

    // Dropping back to a global alert discards the narrowing, so the payload
    // and the UI cannot disagree about who the alert fires for.
    if (scope === 'allAssets') setAlertTags([])
  }

  const handleTagIconClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!tagIconRef.current) return

    const rect = tagIconRef.current.getBoundingClientRect()
    toggleAlertTagsPopout({ position: { x: rect.left, y: rect.bottom } })
  }

  const handleNameChange = (value: string) => {
    setName(value)
    setErrors((prev) => ({ ...prev, name: undefined }))
  }

  const handleContactsChange = (value: string) => {
    setContacts(value)
    setErrors((prev) => ({ ...prev, contacts: undefined }))
  }

  const validate = (): boolean => {
    const newErrors: FormErrors = {}

    if (!name.trim()) {
      newErrors.name = 'Alert name is required'
    }

    const entries = contacts
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    if (entries.length > 0) {
      const invalid = entries.filter((e) => !contactEntrySchema.safeParse(e).success)
      if (invalid.length > 0) {
        newErrors.contacts = `Invalid contact(s): ${invalid.join(', ')}`
      }
    }

    if (selectedTemplate?.defaultFieldValues) {
      const hasInvalid = Object.values(resolvedFieldValues).some((v) => !Number(v) || v <= 0)
      if (hasInvalid) {
        newErrors.fields = ['All fields must be positive whole numbers']
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!selectedTemplate || !user?.organization?.id) return
    if (!validate()) return

    const entries = contacts
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    const emails = entries.filter((e) => emailSchema.safeParse(e).success)
    const phoneNumbers = entries.filter((e) => !emailSchema.safeParse(e).success)

    try {
      if (isEditMode) {
        await updateTrigger({
          enabled: emails.length > 0 || phoneNumbers.length > 0,
          title: name,
          emails,
          phoneNumbers,
          templateName: selectedTemplate.key,
          templateVariables: resolvedFieldValues,
          tags: showTagSelector ? alertTags.map((tag) => tag.id) : [],
        })
      } else {
        await createTrigger({
          enabled: emails.length > 0 || phoneNumbers.length > 0,
          title: name,
          organization: user.organization.id,
          emails,
          phoneNumbers,
          templateName: selectedTemplate.key,
          templateVariables: resolvedFieldValues,
          tags: showTagSelector ? alertTags.map((tag) => tag.id) : [],
        })
      }

      await mutateAlerts()
      closeCreateAlert()
      closeCoreAlert()
    } catch (error) {
      const { formErrors, fieldErrorKeys: keys } = await parseApiErrors(error)
      setErrors((prev) => ({ ...prev, ...formErrors }))
      setFieldErrorKeys(keys)
    }
  }

  useEffect(() => {
    if (!isCoreAlertOpen) {
      setName('')
      setContacts('')
      setFieldValues({})
      setErrors({})
      setFieldErrorKeys(new Set())
      setAlertTags([])
      setAlertScope('allAssets')
      return
    }

    if (editingAlert) {
      setName(editingAlert.title)
      const emails = Array.isArray(editingAlert.emails) ? editingAlert.emails : []
      const phones = Array.isArray(editingAlert.phoneNumbers) ? editingAlert.phoneNumbers : []
      setContacts([...emails, ...phones].join(', '))
      const normalizedVars: AlertTemplateFieldValues = {}
      for (const [key, value] of Object.entries(editingAlert.templateVariables)) {
        normalizedVars[snakeCase(key)] = value
      }
      setFieldValues(normalizedVars)
      setAlertTags(editingAlert.tags ?? [])
      setAlertScope(resolveAlertScope(editingAlert.tags))
    } else {
      setAlertTags([])
      setAlertScope('allAssets')
    }
  }, [isCoreAlertOpen, editingAlert, setAlertTags])

  return {
    isCoreAlertOpen,
    closeCoreAlert,
    selectedTemplate,
    isEditMode,
    isMutating,
    name,
    contacts,
    errors,
    fieldErrorKeys,
    resolvedFieldValues,
    taggableAlert,
    showTagSelector,
    alertScope,
    handleAlertScopeChange,
    tagsUnsupportedMessage,
    alertTags,
    tagIconRef,
    openModal,
    handleTagIconClick,
    handleNameChange,
    handleContactsChange,
    handleFieldChange,
    handleSave,
  }
}
